/**
 * Analyses list filter option catalogs.
 *
 * `ANALYSIS_LAST_SYNC_*` catalogs the GET /analyses/list `last_sync_status`
 * eq values (including `never`) for API clients / legacy tooling. The analyses
 * list UI filters by `last_sync_at` date range instead. Do not reuse connector
 * statuses (`syncing` is not valid for analysis last_sync_status).
 */

export const ANALYSIS_LAST_SYNC_STATUSES = [
  "never",
  "pending",
  "scheduled",
  "running",
  "completed",
  "failed",
  "partial",
  "cancelled",
] as const;

export type AnalysisLastSyncStatusFilter =
  (typeof ANALYSIS_LAST_SYNC_STATUSES)[number];

export const ANALYSIS_LAST_SYNC_FILTER_OPTIONS =
  ANALYSIS_LAST_SYNC_STATUSES.map((status) => ({
    value: status,
    label: status === "never" ? "Never" : titleCaseUnderscore(status),
  }));

export const BOOLEAN_EQ_FILTER_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
] as const;

/**
 * Pass/Fail tokens on the combined Specification filter (`spec_passed` JSON
 * path). Not evaluated is not a chip — named specs with neither Pass nor Fail
 * keep every assigned outcome, including unevaluated rows.
 * Pass is `spec_passed === true` (includes out-of-tolerance; that is target_passed).
 */
export const SENTINEL_SPEC_PASSED_FILTER_OPTIONS = [
  { value: "true", label: "Pass" },
  { value: "false", label: "Fail" },
] as const;

export type SpecPassedFilterValue =
  (typeof SENTINEL_SPEC_PASSED_FILTER_OPTIONS)[number]["value"];

/** `specification_id:is_null` — analyses with no specification assigned. */
export const SPECIFICATION_UNASSIGNED_FILTER_VALUE = "is_null" as const;

/** Prefix so Pass/Fail tokens never collide with specification UUIDs. */
export const SPEC_PASSED_COMPOSITE_PREFIX = "spec_passed:" as const;

/** Closed-chip stand-in when three or more spec names are selected. */
export const SPECIFICATION_NAME_COUNT_PILL = "__spec_count__" as const;

const MAX_VISIBLE_SPEC_NAME_PILLS = 2;

export type AnalysesSpecificationFilterOption = {
  value: string;
  label: string;
};

export function specPassedCompositeValue(
  outcome: SpecPassedFilterValue,
): string {
  return `${SPEC_PASSED_COMPOSITE_PREFIX}${outcome}`;
}

export function parseSpecPassedCompositeValue(
  value: string,
): SpecPassedFilterValue | null {
  if (!value.startsWith(SPEC_PASSED_COMPOSITE_PREFIX)) return null;
  const outcome = value.slice(SPEC_PASSED_COMPOSITE_PREFIX.length);
  if (outcome === "true" || outcome === "false") return outcome;
  return null;
}

export function splitSpecificationFilterValues(
  raw: string | string[] | undefined,
): {
  unassigned: boolean;
  specificationIds: string[];
  specPassed: SpecPassedFilterValue[];
} {
  const values = (raw == null ? [] : Array.isArray(raw) ? raw : [raw]).filter(
    Boolean,
  );
  const specificationIds: string[] = [];
  const specPassed: SpecPassedFilterValue[] = [];
  let unassigned = false;

  for (const value of values) {
    if (value === SPECIFICATION_UNASSIGNED_FILTER_VALUE) {
      unassigned = true;
      continue;
    }
    if (value === "is_not_null") continue;
    if (value.startsWith(SPEC_PASSED_COMPOSITE_PREFIX)) {
      const outcome = parseSpecPassedCompositeValue(value);
      if (outcome && !specPassed.includes(outcome)) specPassed.push(outcome);
      continue;
    }
    specificationIds.push(value);
  }

  return { unassigned, specificationIds, specPassed };
}

export function isSpecificationUnassignedMode(
  raw: string | string[] | undefined,
): boolean {
  const split = splitSpecificationFilterValues(raw);
  return split.unassigned && split.specificationIds.length === 0;
}

/**
 * Pass and/or Fail → `spec_passed` eq/in. Neither selected means no outcome
 * restriction (named specs keep unevaluated rows).
 */
export function specPassedQueryValue(
  specPassed: readonly SpecPassedFilterValue[],
): SpecPassedFilterValue | SpecPassedFilterValue[] | undefined {
  const hasTrue = specPassed.includes("true");
  const hasFalse = specPassed.includes("false");
  if (hasTrue && hasFalse) return ["true", "false"];
  if (hasTrue) return "true";
  if (hasFalse) return "false";
  return undefined;
}

export type SpecificationFilterAction =
  | { type: "toggle-pass" }
  | { type: "toggle-fail" }
  | { type: "toggle-unassigned" }
  | { type: "set-spec-ids"; specIds: readonly string[] }
  | { type: "remove"; token: string }
  | { type: "clear" };

function composeSpecificationFilterValues(args: {
  unassigned: boolean;
  specificationIds: readonly string[];
  pass: boolean;
  fail: boolean;
}): string[] {
  if (args.unassigned) return [SPECIFICATION_UNASSIGNED_FILTER_VALUE];
  const next: string[] = [];
  if (args.pass) next.push(specPassedCompositeValue("true"));
  if (args.fail) next.push(specPassedCompositeValue("false"));
  next.push(...args.specificationIds);
  return next;
}

/** Rebuild Specification toolbar tokens after dropping retired sentinels. */
export function toSpecificationToolbarValues(
  raw: string | string[] | undefined,
): string[] {
  const split = splitSpecificationFilterValues(raw);
  return composeSpecificationFilterValues({
    unassigned: isSpecificationUnassignedMode(raw),
    specificationIds: split.specificationIds,
    pass: split.specPassed.includes("true"),
    fail: split.specPassed.includes("false"),
  });
}

/** Exclusive Not assigned, Pass/Fail toggles, and spec-name selection. */
export function applySpecificationFilterValues(
  current: readonly string[],
  action: SpecificationFilterAction,
): string[] {
  const split = splitSpecificationFilterValues([...current]);
  const pass = split.specPassed.includes("true");
  const fail = split.specPassed.includes("false");
  const specIds = split.specificationIds;
  const unassignedMode = isSpecificationUnassignedMode([...current]);

  switch (action.type) {
    case "clear":
      return [];
    case "toggle-unassigned":
      return unassignedMode ? [] : [SPECIFICATION_UNASSIGNED_FILTER_VALUE];
    case "toggle-pass":
      return composeSpecificationFilterValues({
        unassigned: false,
        specificationIds: unassignedMode ? [] : specIds,
        pass: unassignedMode ? true : !pass,
        fail: unassignedMode ? false : fail,
      });
    case "toggle-fail":
      return composeSpecificationFilterValues({
        unassigned: false,
        specificationIds: unassignedMode ? [] : specIds,
        pass: unassignedMode ? false : pass,
        fail: unassignedMode ? true : !fail,
      });
    case "set-spec-ids":
      return composeSpecificationFilterValues({
        unassigned: false,
        specificationIds: action.specIds,
        pass: unassignedMode ? false : pass,
        fail: unassignedMode ? false : fail,
      });
    case "remove":
      if (action.token === SPECIFICATION_UNASSIGNED_FILTER_VALUE) return [];
      if (action.token === specPassedCompositeValue("true")) {
        return composeSpecificationFilterValues({
          unassigned: false,
          specificationIds: specIds,
          pass: false,
          fail,
        });
      }
      if (action.token === specPassedCompositeValue("false")) {
        return composeSpecificationFilterValues({
          unassigned: false,
          specificationIds: specIds,
          pass,
          fail: false,
        });
      }
      if (action.token === SPECIFICATION_NAME_COUNT_PILL) {
        return composeSpecificationFilterValues({
          unassigned: false,
          specificationIds: [],
          pass,
          fail,
        });
      }
      return composeSpecificationFilterValues({
        unassigned: false,
        specificationIds: specIds.filter((id) => id !== action.token),
        pass,
        fail,
      });
    default:
      return [...current];
  }
}

export type SpecificationFilterTriggerPill = {
  value: string;
  label: string;
};

export function specificationFilterTriggerPills(
  values: readonly string[],
  specLabelById: ReadonlyMap<string, string>,
): SpecificationFilterTriggerPill[] {
  const split = splitSpecificationFilterValues([...values]);
  if (isSpecificationUnassignedMode([...values])) {
    return [
      {
        value: SPECIFICATION_UNASSIGNED_FILTER_VALUE,
        label: "Not assigned",
      },
    ];
  }

  const pills: SpecificationFilterTriggerPill[] = [];
  if (split.specificationIds.length <= MAX_VISIBLE_SPEC_NAME_PILLS) {
    for (const id of split.specificationIds) {
      pills.push({
        value: id,
        label: specLabelById.get(id) ?? id,
      });
    }
  } else if (split.specificationIds.length > 0) {
    pills.push({
      value: SPECIFICATION_NAME_COUNT_PILL,
      label: `${split.specificationIds.length} specs`,
    });
  }
  if (split.specPassed.includes("true")) {
    pills.push({
      value: specPassedCompositeValue("true"),
      label: "Pass",
    });
  }
  if (split.specPassed.includes("false")) {
    pills.push({
      value: specPassedCompositeValue("false"),
      label: "Fail",
    });
  }
  return pills;
}

/**
 * Analyses filter chips shown by default (catalog order). Spec passed is
 * absorbed into Specification. Others stay off until enabled from the
 * funnel visibility menu. A saved custom set still wins; an empty
 * preference, a reset, or a saved copy of the previous default falls
 * back to this list.
 */
export const ANALYSES_DEFAULT_VISIBLE_FILTER_IDS = [
  "sample_location_id",
  "sample_aggregate_id",
  "analysis_type_id",
  "status",
  "priority",
  "lab_id",
  "procedure_id",
  "specification_id",
] as const;

export const ANALYSES_DEFAULT_VISIBLE_FILTER_ID_SET = new Set<string>(
  ANALYSES_DEFAULT_VISIBLE_FILTER_IDS,
);

/**
 * First-visit chips before Procedure and Specification replaced Last Synced At.
 * Browsers that persisted this snapshot (or that snapshot plus Specification)
 * still have Last Synced At on and Procedure off.
 */
const LEGACY_ANALYSES_DEFAULT_VISIBLE_FILTER_IDS = [
  "sample_location_id",
  "sample_aggregate_id",
  "analysis_type_id",
  "status",
  "priority",
  "lab_id",
  "last_sync_at",
] as const;

const LEGACY_ANALYSES_CORE_FILTER_IDS =
  LEGACY_ANALYSES_DEFAULT_VISIBLE_FILTER_IDS.filter(
    (id) => id !== "last_sync_at",
  );

/**
 * Upgrade a saved Analyses funnel that is still the previous default.
 * Returns the new chip list, or null when the save is a real customisation.
 *
 * The previous default is the six core chips plus Last Synced At. Specification
 * may already be on (it was added without removing Last Synced At). Procedure
 * being on, a core chip being off, or any other chip means the user chose it.
 */
export function migrateLegacyAnalysesFilterVisibility(
  stored: readonly string[],
): readonly string[] | null {
  const saved = new Set(stored);
  const allowed = new Set<string>([
    ...LEGACY_ANALYSES_CORE_FILTER_IDS,
    "last_sync_at",
    "procedure_id",
    "specification_id",
  ]);
  if (stored.some((id) => !allowed.has(id))) return null;
  if (LEGACY_ANALYSES_CORE_FILTER_IDS.some((id) => !saved.has(id))) return null;
  if (!saved.has("last_sync_at") || saved.has("procedure_id")) return null;
  return ANALYSES_DEFAULT_VISIBLE_FILTER_IDS;
}

/**
 * Named shells from GET /sentinel/specifications/options. Pass / Fail / Not
 * assigned live on the Specification dropdown toolbar, not in this list.
 */
export function specificationFilterOptions(
  specs: ReadonlyArray<{ specification_id: string; name: string }> = [],
): AnalysesSpecificationFilterOption[] {
  return specs
    .map((p) => ({
      value: p.specification_id,
      label: p.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Pass/Fail tokens on the combined Procedure filter (`procedure_passed` on
 * `procedure_evaluation.passed`). Same operators as `spec_passed`. Not
 * assigned is exclusive and maps to `procedure_id:is_null`.
 */
export const PROCEDURE_UNASSIGNED_FILTER_VALUE = "is_null" as const;

/** Prefix so Pass/Fail tokens never collide with procedure UUIDs. */
export const PROCEDURE_PASSED_COMPOSITE_PREFIX = "procedure_passed:" as const;

/** Closed-chip stand-in when three or more procedure names are selected. */
export const PROCEDURE_NAME_COUNT_PILL = "__procedure_count__" as const;

const MAX_VISIBLE_PROCEDURE_NAME_PILLS = 2;

export type ProcedurePassedFilterValue = SpecPassedFilterValue;

export type AnalysesProcedureFilterOption = {
  value: string;
  label: string;
};

export function procedurePassedCompositeValue(
  outcome: ProcedurePassedFilterValue,
): string {
  return `${PROCEDURE_PASSED_COMPOSITE_PREFIX}${outcome}`;
}

export function parseProcedurePassedCompositeValue(
  value: string,
): ProcedurePassedFilterValue | null {
  if (!value.startsWith(PROCEDURE_PASSED_COMPOSITE_PREFIX)) return null;
  const outcome = value.slice(PROCEDURE_PASSED_COMPOSITE_PREFIX.length);
  if (outcome === "true" || outcome === "false") return outcome;
  return null;
}

export function splitProcedureFilterValues(
  raw: string | string[] | undefined,
): {
  unassigned: boolean;
  procedureIds: string[];
  procedurePassed: ProcedurePassedFilterValue[];
} {
  const values = (raw == null ? [] : Array.isArray(raw) ? raw : [raw]).filter(
    Boolean,
  );
  const procedureIds: string[] = [];
  const procedurePassed: ProcedurePassedFilterValue[] = [];
  let unassigned = false;

  for (const value of values) {
    if (value === PROCEDURE_UNASSIGNED_FILTER_VALUE) {
      unassigned = true;
      continue;
    }
    if (value === "is_not_null") continue;
    if (value.startsWith(PROCEDURE_PASSED_COMPOSITE_PREFIX)) {
      const outcome = parseProcedurePassedCompositeValue(value);
      if (outcome && !procedurePassed.includes(outcome)) {
        procedurePassed.push(outcome);
      }
      continue;
    }
    procedureIds.push(value);
  }

  return { unassigned, procedureIds, procedurePassed };
}

export function isProcedureUnassignedMode(
  raw: string | string[] | undefined,
): boolean {
  const split = splitProcedureFilterValues(raw);
  return split.unassigned && split.procedureIds.length === 0;
}

/**
 * Pass and/or Fail → `procedure_passed` eq/in. Neither selected means no
 * outcome restriction (named procedures keep unevaluated rows).
 */
export function procedurePassedQueryValue(
  procedurePassed: readonly ProcedurePassedFilterValue[],
): ProcedurePassedFilterValue | ProcedurePassedFilterValue[] | undefined {
  return specPassedQueryValue(procedurePassed);
}

export type ProcedureFilterAction =
  | { type: "toggle-pass" }
  | { type: "toggle-fail" }
  | { type: "toggle-unassigned" }
  | { type: "set-procedure-ids"; procedureIds: readonly string[] }
  | { type: "remove"; token: string }
  | { type: "clear" };

function composeProcedureFilterValues(args: {
  unassigned: boolean;
  procedureIds: readonly string[];
  pass: boolean;
  fail: boolean;
}): string[] {
  if (args.unassigned) return [PROCEDURE_UNASSIGNED_FILTER_VALUE];
  const next: string[] = [];
  if (args.pass) next.push(procedurePassedCompositeValue("true"));
  if (args.fail) next.push(procedurePassedCompositeValue("false"));
  next.push(...args.procedureIds);
  return next;
}

/** Rebuild Procedure toolbar tokens after dropping retired sentinels. */
export function toProcedureToolbarValues(
  raw: string | string[] | undefined,
): string[] {
  const split = splitProcedureFilterValues(raw);
  return composeProcedureFilterValues({
    unassigned: isProcedureUnassignedMode(raw),
    procedureIds: split.procedureIds,
    pass: split.procedurePassed.includes("true"),
    fail: split.procedurePassed.includes("false"),
  });
}

/** Exclusive Not assigned, Pass/Fail toggles, and procedure-name selection. */
export function applyProcedureFilterValues(
  current: readonly string[],
  action: ProcedureFilterAction,
): string[] {
  const split = splitProcedureFilterValues([...current]);
  const pass = split.procedurePassed.includes("true");
  const fail = split.procedurePassed.includes("false");
  const procedureIds = split.procedureIds;
  const unassignedMode = isProcedureUnassignedMode([...current]);

  switch (action.type) {
    case "clear":
      return [];
    case "toggle-unassigned":
      return unassignedMode ? [] : [PROCEDURE_UNASSIGNED_FILTER_VALUE];
    case "toggle-pass":
      return composeProcedureFilterValues({
        unassigned: false,
        procedureIds: unassignedMode ? [] : procedureIds,
        pass: unassignedMode ? true : !pass,
        fail: unassignedMode ? false : fail,
      });
    case "toggle-fail":
      return composeProcedureFilterValues({
        unassigned: false,
        procedureIds: unassignedMode ? [] : procedureIds,
        pass: unassignedMode ? false : pass,
        fail: unassignedMode ? true : !fail,
      });
    case "set-procedure-ids":
      return composeProcedureFilterValues({
        unassigned: false,
        procedureIds: action.procedureIds,
        pass: unassignedMode ? false : pass,
        fail: unassignedMode ? false : fail,
      });
    case "remove":
      if (action.token === PROCEDURE_UNASSIGNED_FILTER_VALUE) return [];
      if (action.token === procedurePassedCompositeValue("true")) {
        return composeProcedureFilterValues({
          unassigned: false,
          procedureIds,
          pass: false,
          fail,
        });
      }
      if (action.token === procedurePassedCompositeValue("false")) {
        return composeProcedureFilterValues({
          unassigned: false,
          procedureIds,
          pass,
          fail: false,
        });
      }
      if (action.token === PROCEDURE_NAME_COUNT_PILL) {
        return composeProcedureFilterValues({
          unassigned: false,
          procedureIds: [],
          pass,
          fail,
        });
      }
      return composeProcedureFilterValues({
        unassigned: false,
        procedureIds: procedureIds.filter((id) => id !== action.token),
        pass,
        fail,
      });
    default:
      return [...current];
  }
}

export type ProcedureFilterTriggerPill = {
  value: string;
  label: string;
};

export function procedureFilterTriggerPills(
  values: readonly string[],
  procedureLabelById: ReadonlyMap<string, string>,
): ProcedureFilterTriggerPill[] {
  const split = splitProcedureFilterValues([...values]);
  if (isProcedureUnassignedMode([...values])) {
    return [
      {
        value: PROCEDURE_UNASSIGNED_FILTER_VALUE,
        label: "Not assigned",
      },
    ];
  }

  const pills: ProcedureFilterTriggerPill[] = [];
  if (split.procedureIds.length <= MAX_VISIBLE_PROCEDURE_NAME_PILLS) {
    for (const id of split.procedureIds) {
      pills.push({
        value: id,
        label: procedureLabelById.get(id) ?? id,
      });
    }
  } else if (split.procedureIds.length > 0) {
    pills.push({
      value: PROCEDURE_NAME_COUNT_PILL,
      label: `${split.procedureIds.length} procedures`,
    });
  }
  if (split.procedurePassed.includes("true")) {
    pills.push({
      value: procedurePassedCompositeValue("true"),
      label: "Pass",
    });
  }
  if (split.procedurePassed.includes("false")) {
    pills.push({
      value: procedurePassedCompositeValue("false"),
      label: "Fail",
    });
  }
  return pills;
}

/**
 * Named shells from GET /sentinel/procedures/options. Pass / Fail / Not
 * assigned live on the Procedure dropdown toolbar, not in this list.
 */
export function procedureFilterOptions(
  procedures: ReadonlyArray<{ procedure_id: string; name: string }> = [],
): AnalysesProcedureFilterOption[] {
  return procedures
    .map((p) => ({
      value: p.procedure_id,
      label: p.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function titleCaseUnderscore(value: string): string {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
